import React, { useState, useEffect, useRef } from "react";
import { PitchFormValues } from "@/PitchFormPage/components/Schema/pitchValidationSchema";
import Swal from "sweetalert2";
import Button from "../ui/Button";

interface IntegrationModalProps {
  isOpen: boolean;
  onClose: (data: any) => Promise<boolean>; // Returns true on success, false on failure
  integrationType?: "Airtable" | "Google Sheet";
  closeModal:any
}

// Searchable dropdown component for CSV headers
interface SearchableHeaderSelectProps {
  fieldKey: string;
  value: number | "";
  availableHeaders: { index: number; name: string }[];
  onChange: (fieldKey: string, headerIndex: string) => void;
  disabled: boolean;
  selectRef: (el: HTMLInputElement | null) => void;
}

const SearchableHeaderSelect: React.FC<SearchableHeaderSelectProps> = ({
  fieldKey,
  value,
  availableHeaders,
  onChange,
  disabled,
  selectRef
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  // The list lives inside the modal's scroll box, so near the bottom it would
  // be clipped and spawn an inner scrollbar. Open upward when there's more
  // room above than below.
  const toggleOpen = () => {
    if (!isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      let boundsTop = 0;
      let boundsBottom = window.innerHeight;
      let parent = containerRef.current.parentElement;
      while (parent) {
        const { overflowY } = getComputedStyle(parent);
        if (overflowY === "auto" || overflowY === "scroll" || overflowY === "hidden") {
          const parentRect = parent.getBoundingClientRect();
          boundsTop = parentRect.top;
          boundsBottom = parentRect.bottom;
          break;
        }
        parent = parent.parentElement;
      }
      const spaceBelow = boundsBottom - rect.bottom;
      const spaceAbove = rect.top - boundsTop;
      // 244 = max-h-60 (240px) + mt-1 gap
      setOpenUp(spaceBelow < 244 && spaceAbove > spaceBelow);
    }
    setIsOpen(!isOpen);
  };

  // Get the display name for the current value
  const selectedHeader = availableHeaders.find(h => h.index === value);
  const displayValue = selectedHeader
    ? (selectedHeader.name.length > 25 ? selectedHeader.name.slice(0, 25) + '...' : selectedHeader.name)
    : "";

  // Filter headers based on search term
  const filteredHeaders = availableHeaders.filter(header =>
    header.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (headerIndex: number) => {
    onChange(fieldKey, headerIndex.toString());
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(fieldKey, "");
    setSearchTerm("");
  };

  return (
    <div ref={containerRef} className="relative w-full sm:w-[180px]">
      <div
        className={`border rounded px-2 py-1 text-sm flex items-center justify-between cursor-pointer ${
          disabled ? "bg-neutral-100 text-neutral-400 cursor-not-allowed" : "bg-white text-neutral-700 hover:border-primary-500"
        }`}
        onClick={() => !disabled && toggleOpen()}
      >
        <span className={value === "" ? "text-neutral-400" : "text-neutral-700"}>
          {value === "" ? "Import Field" : displayValue}
        </span>
        <div className="flex items-center gap-1">
          {value !== "" && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="text-xs text-neutral-400 hover:text-primary-700"
            >
              ✕
            </button>
          )}
          <span className="text-neutral-400 text-xs">{isOpen ? "▲" : "▼"}</span>
        </div>
      </div>

      {isOpen && !disabled && (
        <div className={`absolute z-50 w-full bg-white border rounded shadow-lg max-h-60 overflow-hidden ${openUp ? "bottom-full mb-1" : "mt-1"}`}>
          <div className="p-1 border-b">
            <input
              ref={selectRef}
              type="text"
              placeholder="Search columns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-primary-500"
              autoFocus
            />
          </div>
          <div className="overflow-y-auto max-h-48">
            {filteredHeaders.length === 0 ? (
              <div className="px-2 py-2 text-sm text-neutral-400 text-center">No matches found</div>
            ) : (
              filteredHeaders.map((header) => (
                <div
                  key={header.index}
                  onClick={() => handleSelect(header.index)}
                  className={`px-2 py-1.5 text-sm cursor-pointer hover:bg-primary-50 ${
                    header.index === value ? "bg-primary-100 text-primary-700" : "text-neutral-700"
                  }`}
                  title={header.name}
                >
                  {header.name.length > 30 ? header.name.slice(0, 30) + '...' : header.name}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const IntegrationModal: React.FC<IntegrationModalProps> = ({
  isOpen,
  onClose,
  integrationType = "Airtable",
  closeModal
}) => {
  const [step, setStep] = useState(1);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  // Store headers with index to handle duplicates (e.g., multiple "Release Stage" columns)
  const [csvHeaders, setCsvHeaders] = useState<{ index: number; name: string }[]>([]);
  // Map field key to header index (not name) to support duplicate header names
  const [fieldMapping, setFieldMapping] = useState<{ [key: string]: number | "" }>({});
  const selectRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);



  // Only reset when integration type changes, not when modal opens/closes
  // This preserves user's mapping data if API fails
  useEffect(() => {
    setStep(1);
    setCsvFile(null);
    setCsvHeaders([]);
    setFieldMapping({});
    setSelectedDSPs([]);
  }, [integrationType]);

  const handleClose = () => {
    setFieldMapping({});
    closeModal(false)
  };
  const pitchFields: { key: string; label: string }[] = [
  { key: "artistName", label: "Artist Name" },
  { key: "releaseTitle", label: "Release Name" },
  { key: "releaseDate", label: "Release Date" },
  { key: "configuration", label: "Configuration" },
  { key: "releaseUPC", label: "Release UPC" },
  { key: "focusTrackName", label: "Focus Track Name" },
  { key: "trackISRC", label: "Track ISRC" },
  { key: "featuredArtists", label: "Featured Artists" },
  { key: "featuredArtistURI", label: "Featured Artist URI" },
  { key: "altTitles", label: "Alt Titles" },
  { key: "shortDescription", label: "Description" },
  { key: "longDescription", label: "Marketing Drivers" },
  { key: "lyrics", label: "Lyrics" },
  { key: "songDescription", label: "Song Description" },
  { key: "mainGenre", label: "Main Genre" },
  { key: "songMood", label: "Song Mood" },
  { key: "language", label: "Language" },
  { key: "socialsLinks", label: "Socials Links" },
  // spotify
  { key: "dsps.spotify.artistHometown", label: "Artist Hometown" },
  { key: "dsps.spotify.artistCurrentTown", label: "Artist Current Town" },
  { key: "dsps.spotify.spotifyUri", label: "URI" },
  { key: "dsps.spotify.subgenre", label: "Subgenre" },
  { key: "dsps.spotify.musicCulture", label: "Music Culture" },
  { key: "dsps.spotify.pinToPlaylist", label: "Pin To This Is Playlist" },
  { key: "dsps.spotify.instruments", label: "Instruments" },
  { key: "dsps.spotify.songStyle", label: "Song Style" },
  { key: "dsps.spotify.playlist", label: "Spotify Playlist" },
  // spotify notable fields
  { key: "dsps.spotify.pitchToNotable", label: "Pitch Songwriter to Notable" },
  { key: "dsps.spotify.notableSubmissionCategory", label: "Notable Submission Category" },
  { key: "dsps.spotify.notableGlobalMusicProgram", label: "Global Music Program Interest" },
  { key: "dsps.spotify.notableSubgenre", label: "Notable Subgenre" },
  // apple
  { key: "dsps.apple.appleId", label: "Artist ID" },
  { key: "dsps.apple.appleTrackId", label: "Track ID" },
  { key: "dsps.apple.subgenre", label: "Subgenre" },
  { key: "dsps.apple.releaseStage", label: "Release Stage" },
  { key: "dsps.apple.pitchFrom", label: "Pitch From" },
  { key: "dsps.apple.pitchTo", label: "Pitch To" },
  { key: "dsps.apple.hasTimedRelease", label: "Has Timed Release" },
  { key: "dsps.apple.timedReleaseDate", label: "Timed Release Date" },
  { key: "dsps.apple.hasMotionArtwork", label: "Has Motion Artwork" },
  { key: "dsps.apple.hasSpatialAudio", label: "Has Spatial Audio" },
  { key: "dsps.apple.label", label: "Label" },
  { key: "dsps.apple.promotionDate", label: "Promotion Date" },
  { key: "dsps.apple.appleAlbumId", label: "Apple Album ID" },
  { key: "dsps.apple.country", label: "Country" },
  // tidal
  { key: "dsps.tidal.artistPageLink", label: "Page" },
  { key: "dsps.tidal.subgenre", label: "Subgenre" },
  { key: "dsps.tidal.country", label: "Country" },
  { key: "dsps.tidal.labelDistributor", label: "Label Distributor" },
  { key: "dsps.tidal.socialHandles", label: "Socials Links" },
  { key: "dsps.tidal.priorityLevel", label: "Priority Level" },
  { key: "dsps.tidal.dolbyAtmos", label: "Dolby Atmos" },
  { key: "dsps.tidal.localMarket", label: "Local Market" },
  { key: "dsps.tidal.bpm", label: "BPM" },
  { key: "dsps.tidal.confirmation", label: "Pitch Confirmation Email" },
  // amazon
  { key: "dsps.amazon.similarArtists", label: "Similar Artists" },
  { key: "dsps.amazon.listenersLocation", label: "Listeners Location" },
  { key: "dsps.amazon.songCategory", label: "Song Category" },
  { key: "dsps.amazon.subgenre", label: "Subgenre" },
  { key: "dsps.amazon.asin", label: "ASIN" },
  { key: "dsps.amazon.marketingNote", label: "Marketing Note" },
  // deezer
  { key: "dsps.deezer.subgenre", label: "Subgenre" },
  { key: "dsps.deezer.editorialTeam", label: "Editorial Team" },
  { key: "dsps.deezer.priority", label: "Priority" },
  { key: "dsps.deezer.playlist", label: "Playlist" },
  // soundcloud
  { key: "dsps.soundcloud.soundCloudProfileUrl", label: "Profile URL" },
  { key: "dsps.soundcloud.soundCloudContact", label: "Contact" },
  { key: "dsps.soundcloud.subgenre", label: "Subgenre" },
  { key: "dsps.soundcloud.isPremierProgram", label: "Is Premier Program" },
  { key: "dsps.soundcloud.releaseStage", label: "Release Stage" },
  { key: "dsps.soundcloud.freshMusicFriday", label: "Fresh Music Friday" },
  { key: "dsps.soundcloud.playlistConsideration", label: "Playlist Consideration" },
  { key: "dsps.soundcloud.distributor", label: "Distributor" },
  { key: "dsps.soundcloud.label", label: "Label" },
  { key: "dsps.soundcloud.promotionTerritories", label: "Promotion Territories" },
  // audiomack
  { key: "dsps.audiomack.pitchToTrending", label: "Pitch To Trending" },
  { key: "dsps.audiomack.subgenre", label: "Subgenre" },
  // youtube
  { key: "dsps.youtube.promotionSurface", label: "Promotion Surface" },
  { key: "dsps.youtube.songDetails", label: "Release Stage" },
  { key: "dsps.youtube.promotionDate", label: "Promotion Date" },
  { key: "dsps.youtube.territories", label: "Territories" },
  // vevo
  { key: "dsps.vevo.contentType", label: "Content Type" },
  { key: "dsps.vevo.subgenre", label: "Subgenre" },
  { key: "dsps.vevo.contentProvider", label: "Content Provider" },
  { key: "dsps.vevo.premiereDate", label: "Premiere Date" },
  { key: "dsps.vevo.subgenreOther", label: "Subgenre Other" },
  { key: "dsps.vevo.brandAffiliations", label: "Brand Affiliations" },
  { key: "dsps.vevo.categoryRestrictions", label: "Category Restrictions" },
  { key: "dsps.vevo.videoProductPlacement", label: "Video Product Placement" },
  { key: "dsps.vevo.additionalNotes", label: "Additional Notes" },
  { key: "dsps.vevo.previewLink", label: "Preview Link" },
  { key: "dsps.vevo.rep", label: "Rep" },
  { key: "dsps.vevo.repOther", label: "Rep Other" },
  { key: "dsps.vevo.isCtvOnly", label: "Is CTV Only" },
  { key: "dsps.vevo.isRefeature", label: "Is Refeature" },
  // siriusxm
  { key: "dsps.siriusxm.songWriters", label: "Song Writers" },
  { key: "dsps.siriusxm.siriusXmChannel", label: "Channel" },
  { key: "dsps.siriusxm.canadianContent", label: "Canadian Content" },
  // Other fields
  { key: "linkToListen", label: "Link To Listen" },
  { key: "promoPhotos", label: "Promo Photos" },
];


const DSPs = [
  { key: "spotify", label: "Spotify" },
  { key: "apple", label: "Apple" },
  { key: "tidal", label: "TIDAL" },
  { key: "amazon", label: "Amazon" },
  { key: "deezer", label: "Deezer" },
  { key: "soundcloud", label: "SoundCloud" },
  { key: "audiomack", label: "Audiomack" },
  { key: "youtube", label: "YouTube" },
  {key: "siriusxm", label: "SiriusXM" },
  { key : "vevo", label: "Vevo" },
];

  const [selectedDSPs, setSelectedDSPs] = useState<string[]>([]);

  const handleDSPChange = (dspKey: string, checked: boolean) => {
    setSelectedDSPs(prev =>
      checked ? [...prev, dspKey] : prev.filter(k => k !== dspKey)
    );
  };

  const getDSPFromFieldKey = (fieldKey: string) => {
    const match = fieldKey.match(/^dsps\.(\w+)/);
    return match ? match[1] : null;
  };

  const handleFileChange = (file: File | null) => {
    setCsvFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        const headerNames = text.split("\n")[0].split(",").map(h => h.trim());
        // Store headers with index to handle duplicates
        const headersWithIndex = headerNames.map((name, index) => ({ index, name }));
        setCsvHeaders(headersWithIndex);
      };
      reader.readAsText(file);
    } else {
      setCsvHeaders([]);
    }
  };

  if (!isOpen) return null;

  const Stepper = () => (
    <div className="flex items-center mb-6">
      <div className={`flex items-center ${step === 1 ? "font-bold text-primary-700" : "text-neutral-500"}`}>
        <span className="flex items-center justify-center w-6 h-6 mr-2 bg-white border border-primary-700 rounded-full">
          1
        </span>
        Upload CSV
      </div>
      <div className="mx-2 text-neutral-400">—</div>
      <div className={`flex items-center ${step === 2 ? "font-bold text-primary-700" : "text-neutral-500"}`}>
        <span className="flex items-center justify-center w-6 h-6 mr-2 bg-white border border-primary-700 rounded-full">
          2
        </span>
        Mapping
      </div>
    </div>
  );

  const isMappingValid = () => {
    // Required fields
    const required = ["artistName", "releaseTitle", "releaseDate"];
    // Check that all checked fields have a valid index (number, not empty string)
    const allCheckedHaveValue = Object.entries(fieldMapping).every(([_, value]) => typeof value === "number");
    // Check that all required fields are mapped to a valid index
    const allRequiredPresent = required.every(key => fieldMapping.hasOwnProperty(key) && typeof fieldMapping[key] === "number");
    return allCheckedHaveValue && allRequiredPresent;
  };

  const renderStepContent = () => {
    if (step === 1) {
      return (
        <div>
          <p className="mb-4 text-neutral-600">Upload your CSV file to connect with Cadence Pitch.</p>
          <input
            type="file"
            accept=".csv"
            className="block w-full p-2 mb-2 text-sm text-neutral-700 border border-neutral-300 rounded-lg"
            onChange={e => handleFileChange(e.target.files?.[0] || null)}
          />
          {csvFile && (
            <div className="mb-2 text-xs font-semibold text-primary-700">
              Uploaded file: {csvFile.name}
            </div>
          )}
        </div>
      );
    }

    // Mapping step - now uses header index instead of name
    const handleMappingChange = (fieldKey: string, headerIndex: string) => {
      setFieldMapping(prev => ({ ...prev, [fieldKey]: headerIndex === "" ? "" : parseInt(headerIndex) }));
    };

    // Group fields by DSP
    const nonDSPFields = pitchFields.filter(f => !getDSPFromFieldKey(f.key));
    const dspFieldsByDSP: { [dsp: string]: { key: string; label: string }[] } = {};
    DSPs.forEach(dsp => {
      dspFieldsByDSP[dsp.key] = pitchFields.filter(f => getDSPFromFieldKey(f.key) === dsp.key);
    });

    // Get used header indices (excluding current field)
    const getAvailableHeaders = (currentFieldKey: string) => {
      const usedIndices = Object.entries(fieldMapping)
        .filter(([mappedKey, mappedIndex]) => mappedKey !== currentFieldKey && mappedIndex !== "")
        .map(([_, mappedIndex]) => mappedIndex as number);
      return csvHeaders.filter(header => !usedIndices.includes(header.index));
    };

    return (
      <div className="flex gap-6">
        <div className="flex-1 pr-2">
          <p className="mb-4 text-neutral-600">
            Select DSPs and map your CSV columns to Cadence Pitch fields.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3 mb-4">
            {DSPs.map(dsp => (
              <label key={dsp.key} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedDSPs.includes(dsp.key)}
                  onChange={e => handleDSPChange(dsp.key, e.target.checked)}
                  className="accent-primary-700"
                />
                <span className="text-sm font-semibold text-neutral-700">{dsp.label}</span>
              </label>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-0">
            {/* Non-DSP fields */}
            {nonDSPFields.map(({ key, label }) => {
              const isMapped = fieldMapping.hasOwnProperty(key);
              const availableHeaders = getAvailableHeaders(key);

              return (
                <div key={key} className="flex flex-col items-start gap-1 py-2 border-b sm:flex-row sm:items-center sm:justify-between border-neutral-100">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isMapped}
                      onChange={e => {
                        if (e.target.checked) {
                          setFieldMapping(prev => ({ ...prev, [key]: "" }));
                        } else {
                          setFieldMapping(prev => {
                            const copy = { ...prev };
                            delete copy[key];
                            return copy;
                          });
                        }
                      }}
                      className="mr-2 accent-primary-700"
                    />
                    <span className="text-sm text-black">{label}</span>
                  </div>
                  <div className="flex items-center w-full sm:w-auto">
                    <span className="hidden mx-2 text-2xl sm:inline text-neutral-400">&larr;</span>
                    <SearchableHeaderSelect
                      fieldKey={key}
                      value={fieldMapping[key] !== undefined ? fieldMapping[key] : ""}
                      availableHeaders={availableHeaders}
                      onChange={handleMappingChange}
                      disabled={!isMapped}
                      selectRef={el => (selectRefs.current[key] = el)}
                    />
                  </div>
                </div>
              );
            })}

            {/* DSP fields grouped by DSP with heading */}
            {DSPs.filter(dsp => selectedDSPs.includes(dsp.key)).map(dsp => (
              <React.Fragment key={dsp.key}>
                <div className="mt-6 mb-2 text-lg font-bold text-primary-700">{dsp.label}</div>
                {dspFieldsByDSP[dsp.key].map(({ key, label }) => {
                  const isMapped = fieldMapping.hasOwnProperty(key);
                  const availableHeaders = getAvailableHeaders(key);

                  return (
                    <div key={key} className="flex flex-col items-start gap-1 py-2 border-b sm:flex-row sm:items-center sm:justify-between border-neutral-100">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={isMapped}
                          onChange={e => {
                            if (e.target.checked) {
                              setFieldMapping(prev => ({ ...prev, [key]: "" }));
                            } else {
                              setFieldMapping(prev => {
                                const copy = { ...prev };
                                delete copy[key];
                                return copy;
                              });
                            }
                          }}
                          className="mr-2 accent-primary-700"
                        />
                        <span className="text-sm text-black">{label}</span>
                      </div>
                      <div className="flex items-center w-full sm:w-auto">
                        <span className="hidden mx-2 text-2xl sm:inline text-neutral-400">&larr;</span>
                        <SearchableHeaderSelect
                          fieldKey={key}
                          value={fieldMapping[key] !== undefined ? fieldMapping[key] : ""}
                          availableHeaders={availableHeaders}
                          onChange={handleMappingChange}
                          disabled={!isMapped}
                          selectRef={el => (selectRefs.current[key] = el)}
                        />
                      </div>
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const handleFinishClick = async () => {
    const required = ["artistName", "releaseTitle", "releaseDate"];
    // Check for missing required fields - must be mapped to a valid index (number)
    const missing = required.filter(key => !fieldMapping.hasOwnProperty(key) || typeof fieldMapping[key] !== "number");
    if (missing.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Missing Required Fields",
        text: `Please select a value for: ${missing.map(k => pitchFields.find(f => f.key === k)?.label).join(", ")}`,
        timer: 2500,
        showConfirmButton: false,
      });
      const firstMissing = missing[0];
      if (selectRefs.current[firstMissing]) {
        selectRefs.current[firstMissing]?.scrollIntoView({ behavior: "smooth", block: "center" });
        selectRefs.current[firstMissing]?.focus();
      }
      return;
    }
    // Check for other checked fields with no value (not mapped to a valid index)
    const emptyChecked = Object.entries(fieldMapping).find(([_, value]) => typeof value !== "number");
    if (emptyChecked) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Mapping",
        text: "Please select a value for every checked field.",
        timer: 2500,
        showConfirmButton: false,
      });
      const key = emptyChecked[0];
      if (selectRefs.current[key]) {
        selectRefs.current[key]?.scrollIntoView({ behavior: "smooth", block: "center" });
        selectRefs.current[key]?.focus();
      }
      return;
    }

    // Convert indices back to header names for API
    // We use indices internally to handle duplicate column names, but API expects header names
    const mappingWithNames: { [key: string]: string | null } = {};
    Object.entries(fieldMapping).forEach(([fieldKey, headerIndex]) => {
      if (typeof headerIndex === "number") {
        const header = csvHeaders.find(h => h.index === headerIndex);
        mappingWithNames[fieldKey] = header ? header.name : null;
      }
    });

    // Set unmapped DSP fields to null for selected DSPs
    DSPs.forEach(dsp => {
      if (selectedDSPs.includes(dsp.key)) {
        pitchFields
          .filter(f => getDSPFromFieldKey(f.key) === dsp.key)
          .forEach(f => {
            if (!mappingWithNames.hasOwnProperty(f.key)) {
              mappingWithNames[f.key] = null;
            }
          });
      }
    });

    // Submit and wait for API response - only reset on success
    setIsSubmitting(true);
    try {
      const success = await onClose(mappingWithNames);
      if (success) {
        // Only reset state on successful API call
        setStep(1);
        setCsvFile(null);
        setCsvHeaders([]);
        setFieldMapping({});
        setSelectedDSPs([]);
      }
      // If API failed, keep all data preserved so user can retry
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
      <div className="relative w-full max-w-lg sm:max-w-xl lg:max-w-2xl xl:max-w-4xl p-6 sm:p-8 bg-white shadow-lg rounded-xl flex flex-col max-h-[90vh]">
        <button
          className="absolute top-4 right-4 sm:top-6 sm:right-6 p-1 transition-colors rounded-md text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700 border border-transparent"
          onClick={handleClose}
          aria-label="Close"
        >
          &times;
        </button>
        <h2 className="mb-4 text-lg font-semibold text-black">Integrations</h2>
        <Stepper />
        <div className="relative p-4 border rounded-lg bg-neutral-50 overflow-y-auto flex-1 min-h-0">
          {renderStepContent()}
        </div>
        <div className="flex justify-between mt-4 flex-shrink-0">
          <div>
            {step === 2 && (
              <Button
                variant="neutral"
                onClick={() => setStep(1)}
              >
                Back
              </Button>
            )}
          </div>
          <div>
            {step === 1 && (
              <Button
                disabled={!csvFile}
                onClick={() => setStep(2)}
              >
                Next: Mapping
              </Button>
            )}
            {step === 2 && (
              <Button
                loading={isSubmitting}
                onClick={handleFinishClick}
              >
                {isSubmitting ? "Submitting..." : "Finish"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationModal;